import type { ReactElement } from 'react';
import { Text } from '@mantine/core';
import MegaGrid, { type MegaGridColumn } from '@/design/MegaGrid/MegaGrid';
import { makeCurrencyFormatter } from '@/features/licenses/CostFormatting';
import { isSubscriptionCost, rawDescription, recordDescription } from '@/features/licenses/CostRecordDimensions';
import type { LicenseCostRecord } from '@/features/licenses/LicensesApiService';

interface SpendCostRecordsGridProps {
    records: LicenseCostRecord[];
    currency: string;
    persistenceKey: string;
}

/** A MegaGrid of individual cost records — the drill-in table for a provider or a model. Lifted from the old ProviderCostSection. */
export default function SpendCostRecordsGrid({ records, currency, persistenceKey }: SpendCostRecordsGridProps): ReactElement {
    const formatCurrency = makeCurrencyFormatter(currency);
    const defaultGroupBy = pickGroupDimension(records);
    const hasDescriptions = records.some((r) => rawDescription(r) !== null);

    const columns: MegaGridColumn<LicenseCostRecord>[] = [
        { key: 'period', header: 'Period', sortValue: (r) => r.periodStart, render: (r) => <Text size="xs">{periodLabel(r)}</Text> },
        ...(hasDescriptions ?
            [
                {
                    key: 'description',
                    header: 'Description',
                    sortValue: (r) => rawDescription(r) ?? '',
                    groupValue: (r) => rawDescription(r) ?? '',
                    render: (r) => <Text size="xs">{recordDescription(r) ?? '—'}</Text>,
                } satisfies MegaGridColumn<LicenseCostRecord>,
            ]
        :   []),
        { key: 'model', header: 'Model', sortValue: (r) => r.modelId ?? '', render: (r) => <Text size="xs">{r.modelId ?? '—'}</Text> },
        { key: 'sku', header: 'SKU', sortValue: (r) => r.skuId ?? '', render: (r) => <Text size="xs">{r.skuId ?? '—'}</Text> },
        { key: 'workspace', header: 'Workspace', sortValue: (r) => r.workspaceId ?? '', render: (r) => <Text size="xs">{r.workspaceId ?? '—'}</Text> },
        { key: 'source', header: 'Source', sortValue: (r) => r.costSource, render: (r) => <Text size="xs">{r.costSource}</Text> },
        {
            key: 'amount',
            header: 'Amount',
            align: 'right',
            sortValue: (r) => r.amount,
            render: (r) => (
                <Text size="xs" ff="monospace">
                    {formatCurrency(r.amount)}
                </Text>
            ),
        },
    ];

    return (
        <MegaGrid
            persistenceKey={persistenceKey}
            columns={columns}
            rows={records}
            getRowKey={(r, index) => `${r.id}:${index}`}
            defaultGroupBy={defaultGroupBy}
            groupSummary={(rows) => (
                <Text size="xs" fw={600} ff="monospace">
                    {formatCurrency(rows.reduce((sum, r) => sum + r.amount, 0))}
                </Text>
            )}
            clientPageSize={25}
            minWidth={640}
            maxHeight={560}
            searchAccessor={(r) => `${recordDescription(r) ?? ''} ${r.modelId ?? ''} ${r.skuId ?? ''} ${r.workspaceId ?? ''}`}
            searchPlaceholder="Filter records"
            emptyMessage="No cost records."
        />
    );
}

function periodLabel(record: LicenseCostRecord): string {
    const start = new Date(record.periodStart);
    const end = new Date(record.periodEnd);
    const monthSpan =
        start.getUTCDate() === 1 && end.getUTCDate() === 1 && end.getTime() > start.getTime() && (end.getUTCMonth() - start.getUTCMonth() + 12) % 12 === 1;
    return monthSpan ? start.toLocaleString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' }) : record.periodStart.slice(0, 10);
}

/** Group by the dimension matching the records' billing shape: subscription-dominant → description; metered → model/SKU (ties prefer model); flat otherwise. */
function pickGroupDimension(records: LicenseCostRecord[]): string[] {
    const subscriptionCount = records.filter(isSubscriptionCost).length;
    if (subscriptionCount * 2 > records.length) {
        return records.some((r) => rawDescription(r) !== null) ? ['description'] : [];
    }
    const modelCount = records.filter((r) => (r.modelId ?? '') !== '').length;
    const skuCount = records.filter((r) => (r.skuId ?? '') !== '').length;
    if (modelCount === 0 && skuCount === 0) return [];
    return skuCount > modelCount ? ['sku'] : ['model'];
}
